import React, {useEffect, useState} from "react";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {userResetPassword} from "../services/Request";
import {BaseInputPage} from "../components/BaseInputPage";

// MUI Imports
import {
    TextField,
    Box
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";

/** * Reset Page Request
 * @category Pages
 * @returns {JSX.Element} Reset Password Page
 * @description Reset page component with form to enter email, if response is ok it will prompt user to check email for directions to set password.
 * then redirect to login screen after five seconds.
 */
export const ResetPassword = () => {
    const [userEmail, setUserEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [emailTimer, setEmailTimer] = useState(15);
    const currentLocation = useLocation().pathname + useLocation().search;
    const [startTimer, setStartTimer] = useState(false);
    const [resendText, setResendText] = useState(null);
    const [resendHref, setResendHref] = useState(null);

    // If the email is included as a parameter we should automatically submit
    useEffect(() => {
        if (searchParams.get("email")) {
            handleSubmit();
        }
    }, [])

    // Manage the timer and text for the reset link
    useEffect(() => {
        if (emailTimer <= 0) {
            setResendText("RESEND");
            setResendHref(currentLocation);
            setStartTimer(false);
            setEmailTimer(15);
        } else {
            setResendText(`RESEND (${emailTimer}s)`);
            setResendHref(null);
        }
    }, [emailTimer, navigate, currentLocation]);

    // Create a steady countdown using intervals
    useEffect(() => {
        const interval = startTimer && setInterval(() => {
            setEmailTimer((emailTimer) => emailTimer - 1);
        }, 1000);
        return () => clearInterval(interval);
    }, [startTimer])

    async function handleSubmit(e) {
        e?.preventDefault();
        setLoading(true);
        try {
            const email = userEmail || searchParams.get("email");
            setSearchParams({email: email});
            await userResetPassword(email);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
            setStartTimer(true);
        }
    }

    return (
        <>
            <BaseInputPage
                pageTitle="Reset Password"
                titleInfo="Reset Password"
                isSuccessful={startTimer}
                successMsg="If valid, a reset link has been sent to your email."
                linkText={resendText}
                linkHref={resendHref}
            >
                <Box
                    component="form"
                    noValidate
                    onSubmit={handleSubmit}
                >
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label="Email"
                        name="email"
                        onChange={(e) =>
                            setUserEmail(e.target.value)
                        }
                        defaultValue={userEmail || searchParams.get("email")}
                        autoComplete="email"
                        autoFocus
                    />
                    <LoadingButton
                        type="submit"
                        loading={loading}
                        fullWidth
                        variant="contained"
                        sx={{my: 1}}
                        onClick={handleSubmit}
                        disabled={!userEmail}
                    >
                        Reset Password
                    </LoadingButton>
                    <Button fullWidth onClick={() => navigate("/login")}>Back to Login</Button>
                </Box>
            </BaseInputPage>
        </>
    );
};
